<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToDialog'
});
</script>
<template>
	<div v-if="lazy ? modelValue : true" :flex="flex ? flex : null" class="to-dialog" :class="setClass" :style="setStyle" @click.stop="blur">
		<div ref="dialogWrapper" class="to-dialog-wrapper" style="transform: scale(0)" @click.stop>
			<to-header v-if="slide" level="0" class="to-dialog-header" @back="closeHandele">
				<template #title>
					<slot name="title">{{ title }}</slot>
				</template>
			</to-header>

			<div class="to-dialog-content">
				<to-header v-if="(!slide && title) || $slots.title" class="to-dialog-header">
					<template #tip>
						<slot name="tip"></slot>
					</template>
					<slot name="fn"></slot>
					<template #title>
						<slot name="title">{{ title }}</slot>
					</template>
				</to-header>

				<slot />
			</div>

			<to-icon v-if="closable" value="close" class="to-dialog-close" link @click="closeHandele"></to-icon>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, watch, computed, nextTick, onMounted, onActivated, onDeactivated, onBeforeUnmount, getCurrentInstance, CSSProperties } from 'vue'
import { ToHeader } from '@touchvue/components'
import { dialogProps, dialogEmits } from './dialog'



const props = defineProps(dialogProps)
const emit = defineEmits(dialogEmits)

const dialogWrapper = ref<HTMLElement | null>(null)
const dialogHeader = ref<HTMLElement | null>(null)
const closed = ref(true)
const instance = getCurrentInstance()

const setClass = computed(() => {
	const arr = []
	if (props.slide) arr.push('is-slide')
	if (!closed.value) arr.push('is-show')
	if (props.mode) arr.push(`mode-${props.mode}`)
	return arr
})

const setStyle = computed(() => {
	const obj: CSSProperties = {}
	if (props.width) {
		if (!isNaN(Number(props.width))) {
			obj['--width'] = props.width + 'em'
		} else if (props.width === 'full') {
			obj['--width'] = '100%'
		} else {
			obj['--width'] = props.width
		}
	}
	return obj
})

watch(
	() => props.modelValue,
	val => {
		if (val) {
			if (props.beforeOpen) {
				const fn = props.beforeOpen()
				if (fn && typeof (fn as any).then === 'function') {
					;(fn as Promise<boolean>)
						.then(() => {
							nextTick(() => {
								openAction()
							})
						})
						.catch(error => {
							console.log(error)
						})
				} else {
					if (fn) {
						nextTick(() => {
							openAction()
						})
					}
				}
			} else {
				nextTick(() => {
					openAction()
				})
			}
		} else if (!closed.value) {
			colseAction()
		}
	},
	{ immediate: true }
)

function openAction() {
	dialogWrapper.value.style.transform = 'scale(1)'
	emit('beforeOpen')
	if (props.zIndex) {
		;(instance?.proxy?.$el as HTMLElement).style.zIndex = props.zIndex.toString()
	} else {
		;(instance?.proxy?.$el as HTMLElement).style.zIndex = (window as any).$TouchUI?.zIndex.toString()
		;(window as any).$TouchUI ? (window as any).$TouchUI.zIndex++ : ((window as any).$TouchUI = { zIndex: 1 })
	}
	nextTick(() => {
		closed.value = false
		emit('opened')
		if (props.afterOpen && Object.prototype.toString.call(props.afterOpen) === '[object Function]') {
			props.afterOpen()
		}
		initDrag()
	})
	setTimeout(() => {
		dialogWrapper.value.style = ''
	}, 50)
}

function colseAction() {
	if (props.beforeClose) {
		const fn = props.beforeClose()
		if (fn && typeof (fn as any).then === 'function') {
			;(fn as Promise<boolean>)
				.then(() => {
					hide()
					return true
				})
				.catch(error => {
					console.log(error)
				})
		} else {
			if (fn) {
				hide()
				return true
			} else {
				return false
			}
		}
	} else {
		hide()
		return true
	}
}

function init() {
	if (dialogWrapper.value && props.global) {
		document.body.appendChild(dialogWrapper.value.parentNode as Node)
	}
}

function destroy() {
	const el = dialogWrapper.value?.parentNode
	if (el?.parentNode) {
		el.parentNode.removeChild(el)
	}
}

onMounted(init)
onActivated(init)
onDeactivated(destroy)
onBeforeUnmount(destroy)

function hide() {
	dialogWrapper.value.style.transform = 'scale(0)'
	emit('beforeClose')
	closed.value = true
	setTimeout(
		() => {
			emit('update:modelValue', false)
			emit('closed')
			if (props.afterClose && Object.prototype.toString.call(props.afterClose) === '[object Function]') {
				props.afterClose()
			}
		},
		props.slide ? 300 : 100
	)
}

function closeHandele() {
	// hide()
	if (colseAction()) {
		emit('close-button-click')
	}
}

function blur() {
	if (props.closeOnBlur && props.closable) colseAction()
}

function initDrag() {
	if (!props.draggable || !dialogHeader.value || !dialogWrapper.value) return
	const dragDom = dialogWrapper.value
	const headerEl = dialogHeader.value
	const bodyW = document.body.clientWidth
	const bodyH = document.body.clientHeight

	headerEl.style.cursor = 'move'
	let startX: number, startY: number, styL: number, styT: number

	const getStyle = window.getComputedStyle(dragDom)

	function onStart(e: MouseEvent | TouchEvent) {
		const ev = 'touches' in e ? e.touches[0] : e
		startX = ev.clientX
		startY = ev.clientY

		if (getStyle.left.includes('%')) {
			styL = (bodyW * parseFloat(getStyle.left)) / 100
			styT = (bodyH * parseFloat(getStyle.top)) / 100
		} else {
			styL = parseFloat(getStyle.left || '0')
			styT = parseFloat(getStyle.top || '0')
		}

		document.onmousemove = onMove
		document.onmouseup = onEnd
		document.ontouchmove = onMove as any
		document.ontouchend = onEnd as any
	}

	function onMove(e: MouseEvent | TouchEvent) {
		const ev = 'touches' in e ? e.touches[0] : e
		const deltaX = ev.clientX - startX
		const deltaY = ev.clientY - startY
		let left = styL + deltaX
		let top = styT + deltaY

		left = Math.min(Math.max(left, -bodyW / 2), bodyW - dragDom.clientWidth / 2)
		top = Math.min(Math.max(top, -bodyH / 2), bodyH - dragDom.clientHeight)

		dragDom.style.left = `${left}px`
		dragDom.style.top = `${top}px`
	}

	function onEnd() {
		document.onmousemove = null
		document.onmouseup = null
		document.ontouchmove = null
		document.ontouchend = null
	}

	headerEl.addEventListener('mousedown', onStart as any, true)
	headerEl.addEventListener('touchstart', onStart as any, true)
}

defineExpose({
	hide
})
</script>
